import { memo } from 'react';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

interface LiteralInputProps {
  value: string;
  onChange: (value: string) => void;
  allowedValues?: string[];
  allowedPattern?: string | null;
  disabled?: boolean;
  error?: string;
}

function validateLiteral(value: string, allowedValues: string[], allowedPattern: string | null): string {
  if (allowedValues.length > 0 && !allowedValues.includes(value)) {
    return `Must be one of: ${allowedValues.join(', ')}`;
  }
  if (allowedPattern && value !== '') {
    try {
      if (!new RegExp(`^${allowedPattern}$`).test(value)) {
        return `Must match pattern: ${allowedPattern}`;
      }
    } catch {
      // invalid pattern from backend — skip
    }
  }
  return '';
}

const LiteralInput = memo<LiteralInputProps>(({
  value,
  onChange,
  allowedValues = [],
  allowedPattern = null,
  disabled = false,
  error,
}) => {
  const inlineError = error || (value !== '' ? validateLiteral(value, allowedValues, allowedPattern) : '');

  if (allowedValues.length > 0) {
    return (
      <TextField
        select
        size="small"
        label="Value"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        error={Boolean(inlineError)}
        helperText={inlineError}
        sx={{ minWidth: 200 }}
      >
        {allowedValues.map((v) => (
          <MenuItem key={v} value={v} sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
            {v}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  return (
    <TextField
      size="small"
      label="Value"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      error={Boolean(inlineError)}
      helperText={inlineError || (allowedPattern ? `Pattern: ${allowedPattern}` : '')}
      sx={{ minWidth: 200, '& input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
    />
  );
});

LiteralInput.displayName = 'LiteralInput';
export default LiteralInput;
